import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiRefreshCw, FiEye, FiEyeOff, FiSettings } from 'react-icons/fi';
import { statusLeadOpcoes } from '../../../mock/clientes';
import { listarLeads, criarLead, atualizarLead, excluirLead } from '../../../services/leads';
import LeadCard from './components/LeadCard';
import LeadForm from './components/LeadForm';
import LeadDetalhe from './components/LeadDetalhe';
import ConfigLeads from './components/ConfigLeads';
import styles from './Lead.module.css';

function Leads() {
  const [leads, setLeads] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [erroSalvar, setErroSalvar] = useState('');
  const [leadAtivo, setLeadAtivo] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [leadEdicao, setLeadEdicao] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [mostrarConvertidos, setMostrarConvertidos] = useState(false);
  const [mostrarConfig, setMostrarConfig] = useState(false);

  const carregarLeads = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const data = await listarLeads();
      setLeads(data);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregarLeads();
  }, [carregarLeads]);

  /* vindo do "Novo cliente" do menu Clientes: abre o lead novo */
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (new URLSearchParams(location.search).get('novo') !== '1') return;
    setLeadAtivo(null);
    setLeadEdicao(null);
    setErroSalvar('');
    setMostrarForm(true);
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, navigate]);

  /* ——— handlers ——— */

  function handleNovoLead() {
    setLeadEdicao(null);
    setErroSalvar('');
    setMostrarForm(true);
  }

  function handleEditar(lead) {
    setLeadAtivo(null);
    setLeadEdicao(lead);
    setErroSalvar('');
    setMostrarForm(true);
  }

  function handleCancelarForm() {
    setMostrarForm(false);
    setLeadEdicao(null);
    setErroSalvar('');
  }

  async function handleSalvar(dados) {
    setSalvando(true);
    setErroSalvar('');
    try {
      if (leadEdicao) {
        const atualizado = await atualizarLead(leadEdicao.id, dados);
        setLeads((prev) => prev.map((l) => (l.id === leadEdicao.id ? atualizado : l)));
      } else {
        const novo = await criarLead(dados);
        setLeads((prev) => [novo, ...prev]);
      }
      setMostrarForm(false);
      setLeadEdicao(null);
    } catch (e) {
      setErroSalvar(e.message);
    } finally {
      setSalvando(false);
    }
  }

  /* grava sem fechar a tela (lead novo ao chegar em Documentos e Contrato):
     a partir daí o formulário passa a editar o lead já criado */
  async function handleSalvarRascunho(dados) {
    const salvo = leadEdicao ? await atualizarLead(leadEdicao.id, dados) : await criarLead(dados);
    setLeads((prev) => (prev.some(l => l.id === salvo.id) ? prev.map(l => (l.id === salvo.id ? salvo : l)) : [salvo, ...prev]));
    setLeadEdicao(salvo);
    return salvo;
  }

  async function handleExcluir(id) {
    setErro('');
    try {
      await excluirLead(id);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      setLeadAtivo(null);
    } catch (e) {
      setErro(e.message);
    }
  }

  /* ——— filtros ——— */

  const isAprovado = (l) => l.status === 'Aprovado' || l.status === 'Convertido';
  const totalAprovados = leads.filter(isAprovado).length;

  const leadsFiltrados = leads.filter((l) => {
    if (!mostrarConvertidos && isAprovado(l)) return false;
    const termo = busca.toLowerCase();
    const matchBusca =
      !termo ||
      l.nome.toLowerCase().includes(termo) ||
      (l.empresa || '').toLowerCase().includes(termo);
    const matchStatus = !filtroStatus || l.status === filtroStatus;
    return matchBusca && matchStatus;
  });

  /* ——— detalhe ——— */

  if (leadAtivo) {
    return (
      <>
        <LeadDetalhe
          lead={leadAtivo}
          onVoltar={() => setLeadAtivo(null)}
          onEditar={handleEditar}
          onExcluir={handleExcluir}
        />
        {mostrarForm && (
          <LeadForm
            lead={leadEdicao}
            salvando={salvando}
            erro={erroSalvar}
            onSalvar={handleSalvar}
            onSalvarRascunho={handleSalvarRascunho}
            onCancelar={handleCancelarForm}
          />
        )}
      </>
    );
  }

  /* ——— lista ——— */

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.titulo}>Leads</h1>
        <div className={styles.headerAcoes}>
          <button className={styles.btnConfig} onClick={() => setMostrarConfig(true)}>
            <FiSettings /> Modelos e checklist
          </button>
          <button className={styles.btnNovo} onClick={handleNovoLead}>
            <FiPlus /> Novo Lead
          </button>
        </div>
      </div>

      {erro && (
        <div className={styles.erroFaixa}>
          {erro}
          <button className={styles.erroBtnRecarregar} onClick={carregarLeads}>
            <FiRefreshCw /> Tentar novamente
          </button>
        </div>
      )}

      <div className={styles.filtros}>
        <div className={styles.buscaWrapper}>
          <FiSearch className={styles.buscaIcon} />
          <input
            type="text"
            placeholder="Buscar por nome ou empresa..."
            className={styles.buscaInput}
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <select
          className={styles.filtroSelect}
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
        >
          <option value="">Todos os status</option>
          {statusLeadOpcoes.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {totalAprovados > 0 && (
          <button
            className={`${styles.btnConvertidos} ${mostrarConvertidos ? styles.btnConvertidosAtivo : ''}`}
            onClick={() => setMostrarConvertidos(v => !v)}
          >
            {mostrarConvertidos ? <FiEyeOff size={14} /> : <FiEye size={14} />}
            {mostrarConvertidos ? 'Ocultar aprovados' : `Aprovados (${totalAprovados})`}
          </button>
        )}
      </div>

      {carregando ? (
        <p className={styles.empty}>Carregando leads...</p>
      ) : leadsFiltrados.length === 0 ? (
        <p className={styles.empty}>
          {busca || filtroStatus ? 'Nenhum lead encontrado para o filtro.' : 'Nenhum lead cadastrado ainda.'}
        </p>
      ) : (
        <div className={styles.grid}>
          {leadsFiltrados.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              onEditar={handleEditar}
              onVerDetalhes={setLeadAtivo}
            />
          ))}
        </div>
      )}

      {mostrarForm && (
        <LeadForm
          lead={leadEdicao}
          salvando={salvando}
          erro={erroSalvar}
          onSalvar={handleSalvar}
          onSalvarRascunho={handleSalvarRascunho}
          onCancelar={handleCancelarForm}
        />
      )}
      {mostrarConfig && <ConfigLeads onFechar={() => setMostrarConfig(false)} />}
    </div>
  );
}

export default Leads;
